import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CaretRight, Globe, MapPin, Wine, Leaf, MagnifyingGlassPlus } from '@phosphor-icons/react';

const InfoOverviewPage = () => {
  const navigate = useNavigate();

  const countries = [
    {
      name: 'France',
      slug: 'france',
      description: 'Le berceau du vin, avec ses appellations prestigieuses',
      regions: ['Bordeaux', 'Provence', 'Bourgogne', 'Champagne', 'Loire', 'Rhône', 'Alsace', 'Jura'],
      featuredGrapes: ['Chardonnay', 'Cabernet Sauvignon', 'Merlot', 'Pinot Noir', 'Syrah']
    },
    {
      name: 'Espagne',
      slug: 'spain',
      description: 'Terre de vins rouges puissants et de cépages autochtones',
      regions: ['Rioja', 'Ribera del Duero', 'Navarra', 'Priorat'],
      featuredGrapes: ['Tempranillo', 'Graciano', 'Garnacha', 'Verdejo', 'Albariño']
    },
    {
      name: 'Portugal',
      slug: 'portugal',
      description: 'Pays des vins fortifiés et des cépages uniques',
      regions: ['Douro', 'Alentejo', 'Dão', 'Vinho Verde', 'Bairrada', 'Lisboa', 'Setúbal', 'Madeira'],
      featuredGrapes: ['Touriga Nacional', 'Alvarinho', 'Baga', 'Encruzado']
    },
    {
      name: 'Italie',
      slug: 'italy',
      description: 'Diversité viticole millénaire et cépages autochtones',
      regions: ['Toscane', 'Piémont', 'Vénétie', 'Sicile', 'SuperTuscan'],
      featuredGrapes: ['Sangiovese', 'Nebbiolo', 'Pinot Grigio', 'Nero d\'Avola']
    },
    {
      name: 'Argentine',
      slug: 'argentina',
      description: 'Terre du Malbec et des vins d\'altitude des Andes',
      regions: ['Mendoza', 'Patagonie', 'Salta', 'San Juan', 'La Rioja'],
      featuredGrapes: ['Malbec', 'Torrontés', 'Cabernet Sauvignon', 'Chardonnay']
    },
    {
      name: 'Chili',
      slug: 'chile',
      description: 'Vins de qualité entre océan et montagnes',
      regions: ['Maipo', 'Colchagua', 'Curicó', 'Maule', 'Casablanca'],
      featuredGrapes: ['Carmenère', 'Cabernet Sauvignon', 'Sauvignon Blanc', 'Chardonnay']
    },
    {
      name: 'Australie',
      slug: 'australia',
      description: 'Innovation viticole et vins du Nouveau Monde',
      regions: ['Barossa Valley', 'Hunter Valley', 'Margaret River', 'McLaren Vale', 'Yarra Valley'],
      featuredGrapes: ['Shiraz', 'Chardonnay', 'Cabernet Sauvignon', 'Riesling']
    },
    {
      name: 'Autriche',
      slug: 'austria',
      description: 'Vins blancs élégants et vins liquoreux prestigieux',
      regions: ['Niederösterreich', 'Burgenland', 'Steiermark', 'Wien'],
      featuredGrapes: ['Grüner Veltliner', 'Riesling', 'Zweigelt', 'Blaufränkisch']
    },
    {
      name: 'Suisse',
      slug: 'switzerland',
      description: 'Vins de terroir entre lacs et montagnes',
      regions: ['Valais', 'Vaud', 'Genève', 'Tessin'],
      featuredGrapes: ['Chasselas', 'Pinot Noir', 'Gamay', 'Merlot']
    },
    {
      name: 'Mexique',
      slug: 'mexico',
      description: 'Berceau de la viticulture américaine',
      regions: ['Baja California', 'Coahuila', 'Querétaro'],
      featuredGrapes: ['Nebbiolo', 'Cabernet Sauvignon', 'Chardonnay', 'Chenin Blanc']
    },
    {
      name: 'Allemagne',
      slug: 'germany',
      description: 'Terre du Riesling et des vins liquoreux',
      regions: ['Rheinhessen', 'Pfalz', 'Baden', 'Mosel', 'Württemberg'],
      featuredGrapes: ['Riesling', 'Spätburgunder', 'Müller-Thurgau', 'Silvaner']
    }
  ];

  const categories = [
    {
      name: 'Pays Viticoles',
      description: 'Découvrez les principaux pays producteurs de vin',
      icon: <Globe size={32} color="#4B164C" weight="light" />,
      link: '/info/countries'
    },
    {
      name: 'Régions',
      description: 'Explorez les régions viticoles par pays',
      icon: <MapPin size={32} color="#4B164C" weight="light" />,
      link: '/info/regions'
    },
    {
      name: 'Cépages',
      description: 'Apprenez sur les cépages du monde entier',
      icon: <Leaf size={32} color="#4B164C" weight="light" />,
      link: '/info/cepages'
    },
    {
      name: 'Styles de Vin',
      description: 'Comprendre les différents styles de vin',
      icon: <Wine size={32} color="#4B164C" weight="light" />,
      link: '/info/styles'
    }
  ];

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          color: '#4B164C', 
          marginBottom: '10px',
          fontWeight: '700'
        }}>
          Guide du Vin
        </h1>
        <p style={{ 
          fontSize: '1.2rem', 
          color: '#666', 
          maxWidth: '600px', 
          margin: '0 auto',
          lineHeight: '1.6'
        }}>
          Votre ressource complète pour découvrir le monde du vin : pays, régions, cépages et styles
        </p>
        <button
          onClick={() => navigate('/search')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#4B164C',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            transition: 'background 0.2s',
            margin: '20px auto 0'
          }}
          onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
          onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
        >
          <MagnifyingGlassPlus size={18} />
          Rechercher des vins
        </button>
      </div>

      {/* Categories */}
      <div style={{ marginBottom: '50px' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4B164C', 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          Explorez par Catégorie
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '20px' 
        }}>
          {categories.map((category, index) => (
            <Link
              key={index}
              to={category.link}
              style={{
                display: 'block',
                padding: '25px',
                background: '#f8f6fa',
                borderRadius: '12px',
                textDecoration: 'none',
                color: 'inherit',
                border: '2px solid #e8e0e8',
                transition: 'all 0.3s ease',
                textAlign: 'center'
              }}
              onMouseOver={e => {
                e.currentTarget.style.borderColor = '#4B164C';
                e.currentTarget.style.background = '#f0e6f0';
              }}
              onMouseOut={e => {
                e.currentTarget.style.borderColor = '#e8e0e8';
                e.currentTarget.style.background = '#f8f6fa';
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                marginBottom: '15px',
                height: '60px'
              }}>
                {category.icon}
              </div>
              <h3 style={{ 
                fontSize: '1.3rem', 
                color: '#4B164C', 
                marginBottom: '10px',
                fontWeight: '600'
              }}>
                {category.name}
              </h3>
              <p style={{ 
                color: '#666', 
                fontSize: '0.95rem',
                lineHeight: '1.5'
              }}>
                {category.description}
              </p>
            </Link>
          ))}
        </div>
      </div>

      {/* Countries Overview */}
      <div>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4B164C', 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          Pays Viticoles Principaux
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '25px' 
        }}>
          {countries.map((country, index) => (
            <div
              key={index}
              style={{
                padding: '25px',
                background: '#fff',
                borderRadius: '12px',
                border: '2px solid #e8e0e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  background: '#4B164C', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  marginRight: '15px'
                }}>
                  {country.name.charAt(0)}
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: '1.4rem', 
                    color: '#4B164C', 
                    margin: '0 0 5px 0',
                    fontWeight: '600'
                  }}>
                    {country.name}
                  </h3>
                  <p style={{ 
                    color: '#666', 
                    fontSize: '0.9rem',
                    margin: '0',
                    lineHeight: '1.4'
                  }}>
                    {country.description}
                  </p>
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <h4 style={{ 
                  fontSize: '1rem', 
                  color: '#4B164C', 
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Régions principales :
                </h4>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px' 
                }}>
                  {country.regions.slice(0, 4).map((region, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '4px 8px',
                        background: '#f0e6f0',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        color: '#4B164C'
                      }}
                    >
                      {region}
                    </span>
                  ))}
                  {country.regions.length > 4 && (
                    <span style={{
                      padding: '4px 8px',
                      background: '#e8e0e8',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      color: '#666'
                    }}>
                      +{country.regions.length - 4} autres
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ 
                  fontSize: '1rem', 
                  color: '#4B164C', 
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Cépages emblématiques :
                </h4>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px' 
                }}>
                  {country.featuredGrapes.slice(0, 3).map((grape, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '4px 8px',
                        background: '#e8e0e8',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        color: '#666'
                      }}
                    >
                      {grape}
                    </span>
                  ))}
                </div>
              </div>

              <Link
                to={`/info/countries/${country.slug}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#4B164C',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
                onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
              >
                Découvrir {country.name}
                <CaretRight size={16} />
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div style={{ 
        marginTop: '50px', 
        padding: '30px', 
        background: '#f8f6fa', 
        borderRadius: '12px',
        textAlign: 'center'
      }}>
        <h3 style={{ 
          fontSize: '1.3rem', 
          color: '#4B164C', 
          marginBottom: '15px',
          fontWeight: '600'
        }}>
          Navigation Rapide
        </h3>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '15px', 
          justifyContent: 'center' 
        }}>
          <Link
            to="/info/countries"
            style={{
              padding: '8px 16px',
              background: '#4B164C',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            Tous les Pays
          </Link>
          <Link
            to="/info/regions"
            style={{
              padding: '8px 16px',
              background: '#6d2c7a',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            Toutes les Régions
          </Link>
          <Link
            to="/info/cepages"
            style={{
              padding: '8px 16px',
              background: '#8a4a8a',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            Tous les Cépages
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InfoOverviewPage; 